import { useEffect, useRef, useState } from 'react';

const CTAFinalSobre = () => {
  const [stage, setStage] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Staggered reveal stages
          const timers = [
            setTimeout(() => setStage(1), 100),
            setTimeout(() => setStage(2), 400),
            setTimeout(() => setStage(3), 700),
            setTimeout(() => setStage(4), 1000),
            setTimeout(() => setStage(5), 1300),
            setTimeout(() => setStage(6), 1600),
            setTimeout(() => setStage(7), 1900),
          ];
          return () => timers.forEach(clearTimeout);
        }
      },
      { threshold: 0.2 },
    );

    const current = sectionRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-32 overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/e71d6ec951f4789ff0f0e5b84d6249ea.jpg"
          alt="Comece sua jornada de transformação"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/75 to-black/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40" />
      </div>

      {/* Decorative elements */}
      <div className="absolute top-16 left-1/4 w-2 h-2 bg-gold-500/40 rounded-full animate-float" />
      <div
        className="absolute top-28 right-1/3 w-1.5 h-1.5 bg-gold-400/30 rounded-full animate-float"
        style={{ animationDelay: '2s' }}
      />
      <div
        className="absolute bottom-20 left-1/3 w-1 h-1 bg-gold-300/40 rounded-full animate-float"
        style={{ animationDelay: '3.5s' }}
      />

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          {/* Badge - expanding lines + fade */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div
              className="h-px bg-gradient-to-r from-transparent to-gold-500/50 transition-all duration-1000 ease-out"
              style={{
                width: stage >= 1 ? '4rem' : '0rem',
                opacity: stage >= 1 ? 1 : 0,
              }}
            />
            <span
              className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-400/70 transition-all duration-700 ease-out"
              style={{
                opacity: stage >= 1 ? 1 : 0,
                transform: stage >= 1 ? 'translateY(0)' : 'translateY(-12px)',
                filter: stage >= 1 ? 'blur(0px)' : 'blur(3px)',
              }}
            >
              Sua jornada começa agora
            </span>
            <div
              className="h-px bg-gradient-to-l from-transparent to-gold-500/50 transition-all duration-1000 ease-out"
              style={{
                width: stage >= 1 ? '4rem' : '0rem',
                opacity: stage >= 1 ? 1 : 0,
              }}
            />
          </div>

          {/* Heading - blur + scale reveal */}
          <h2
            className="font-['Playfair_Display'] text-4xl lg:text-6xl text-white font-light leading-tight mb-6 transition-all duration-900 ease-out"
            style={{
              opacity: stage >= 2 ? 1 : 0,
              transform: stage >= 2 ? 'translateY(0) scale(1)' : 'translateY(25px) scale(0.95)',
              filter: stage >= 2 ? 'blur(0px)' : 'blur(8px)',
            }}
          >
            Escreva hoje para o{' '}
            <span
              className="italic text-gold-400 block mt-2 transition-all duration-800 ease-out"
              style={{
                opacity: stage >= 3 ? 1 : 0,
                transform: stage >= 3 ? 'translateX(0)' : 'translateX(-30px)',
                filter: stage >= 3 ? 'blur(0px)' : 'blur(4px)',
              }}
            >
              seu eu do futuro
            </span>
          </h2>

          {/* Description - fade up */}
          <p
            className="font-['Montserrat'] text-sm lg:text-base text-white/45 max-w-xl mx-auto leading-relaxed font-light tracking-wide mb-12 transition-all duration-700 ease-out"
            style={{
              opacity: stage >= 3 ? 1 : 0,
              transform: stage >= 3 ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: '150ms',
            }}
          >
            Não espere o momento perfeito. O momento é agora. Sua versão futura
            está esperando por uma mensagem sua — cheia de amor, coragem e
            esperança.
          </p>

          {/* CTA Buttons - scale bounce */}
          <div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 transition-all duration-700 ease-out"
            style={{
              opacity: stage >= 4 ? 1 : 0,
              transform: stage >= 4 ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.9)',
            }}
          >
            <a
              href="/dashboard"
              className={`group relative inline-flex items-center gap-3 bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-white font-['Montserrat'] text-sm font-semibold px-10 py-4 rounded-lg transition-all duration-500 shadow-[0_8px_30px_rgba(201,168,76,0.25)] hover:shadow-[0_12px_40px_rgba(201,168,76,0.35)] whitespace-nowrap cursor-pointer overflow-hidden ${stage >= 5 ? 'animate-cta-glow' : ''}`}
            >
              {/* Shimmer sweep */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              <i className="ri-quill-pen-line text-lg w-5 h-5 flex items-center justify-center relative z-10" />
              <span className="relative z-10">Escrever minha carta agora</span>
              <i className="ri-arrow-right-line text-lg w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-300 relative z-10" />
            </a>

            <a
              href="/galeria"
              className="inline-flex items-center gap-2 font-['Montserrat'] text-sm text-gold-400/80 hover:text-gold-300 transition-all duration-300 tracking-wide cursor-pointer whitespace-nowrap px-6 py-4 rounded-lg border border-gold-500/20 hover:border-gold-500/40 hover:bg-gold-500/5"
              style={{
                opacity: stage >= 4 ? 1 : 0,
                transform: stage >= 4 ? 'translateX(0)' : 'translateX(20px)',
                transition: 'opacity 0.6s ease-out, transform 0.6s ease-out, color 0.3s, border-color 0.3s, background-color 0.3s',
                transitionDelay: stage < 5 ? '200ms' : '0ms',
              }}
            >
              <i className="ri-gallery-line text-lg w-5 h-5 flex items-center justify-center" />
              Ver cartas inspiradoras
            </a>
          </div>

          {/* Botão Press Kit */}
          <div
            className="transition-all duration-700 ease-out"
            style={{
              opacity: stage >= 6 ? 1 : 0,
              transform: stage >= 6 ? 'translateY(0)' : 'translateY(15px)',
            }}
          >
            <a
              href="/imprensa"
              className="inline-flex items-center gap-2 font-['Montserrat'] text-xs text-gold-400/60 hover:text-gold-400 transition-colors duration-300 cursor-pointer tracking-wide whitespace-nowrap"
            >
              <i className="ri-newspaper-line text-sm w-4 h-4 flex items-center justify-center" />
              Ver Press Kit Completo
            </a>
          </div>

          {/* Trust badges - staggered fade */}
          <div className="flex items-center justify-center gap-8 mt-14">
            {[
              { icon: 'ri-shield-check-line', text: '100% privado', delay: 0 },
              { icon: 'ri-lock-line', text: 'Dados criptografados', delay: 100 },
              { icon: 'ri-heart-line', text: 'Feito com amor', delay: 200 },
            ].map((badge, i) => (
              <div
                key={i}
                className="flex items-center gap-2 transition-all duration-600 ease-out"
                style={{
                  opacity: stage >= 7 ? 1 : 0,
                  transform: stage >= 7 ? 'translateY(0)' : 'translateY(12px)',
                  transitionDelay: `${badge.delay}ms`,
                }}
              >
                <i className={`${badge.icon} text-gold-500/50 text-sm w-4 h-4 flex items-center justify-center`} />
                <span className="font-['Montserrat'] text-[10px] text-white/30 tracking-wider uppercase">
                  {badge.text}
                </span>
                {i < 2 && <div className="w-px h-3 bg-white/10 ml-6" />}
              </div>
            ))}
          </div>

          {/* Bottom golden line */}
          <div
            className="mx-auto mt-10 h-px bg-gradient-to-r from-transparent via-gold-500/40 to-transparent transition-all duration-1200 ease-out"
            style={{
              width: stage >= 7 ? '16rem' : '0rem',
              opacity: stage >= 7 ? 1 : 0,
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default CTAFinalSobre;
